"use client";

import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import {
  Box,
  Button,
  CircularProgress,
  Typography,
  useTheme,
} from "@mui/material";
import { tokens } from "../../theme";
import { useEffect, useState } from "react";
import { ReferralProgram } from "./types/view.referral";
import { getReferralProgram } from "./utils/refferals";
import ReferralProfileCard from "./view-referral/ReferralProfileCard";
import ReferralInfoCard from "./view-referral/ReferralInfoCard";
import DocumentsCard from "./view-referral/DocumentsCard";
import CommissionCard from "./view-referral/CommissionCard";
import NotesCard from "./view-referral/NotesCard";
import ReferralsTable from "./view-referral/ReferralTable";
import { useNavigate, useParams } from "react-router-dom";

export default function ReferralDetailsPage() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const params = useParams();
  const navigate = useNavigate();
  const id = params?.id as string;

  const [data, setData] = useState<ReferralProgram | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchReferral = async () => {
      try {
        setLoading(true);
        setError("");
        const response = await getReferralProgram(id);
        setData(response);
      } catch (err) {
        console.error("Failed to fetch referral details", err);
        setError("Failed to load referral details");
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchReferral();
    }
  }, [id]);

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "grid",
          placeItems: "center",
          backgroundColor: "#EEF1F5",
        }}
      >
        <Box sx={{ textAlign: "center" }}>
          <CircularProgress />
          <Typography sx={{ mt: 2 }}>Loading referral details...</Typography>
        </Box>
      </Box>
    );
  }

  if (error || !data) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "grid",
          placeItems: "center",
        }}
      >
        <Box sx={{ textAlign: "center" }}>
          <Typography sx={{ fontSize: "1.2rem", fontWeight: 600, mb: 2 }}>
            {error || "No data found"}
          </Typography>
          <Button variant="contained" onClick={() => navigate(-1)}>
            Go Back
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh" }}>
      <Button
        startIcon={<ArrowBackIosNewIcon sx={{ fontSize: 14 }} />}
        onClick={() => navigate(-1)}
        sx={{
          mb: 2.5,
          color: "#6E7885",
          backgroundColor: colors.primary[900],
          borderRadius: "999px",
          px: 2,
          textTransform: "none",
        }}
      >
        Back
      </Button>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" },
          gap: 2,
          alignItems: "start",
        }}
      >
        <ReferralProfileCard data={data} />
        <CommissionCard referralProgramId={id} />

        <ReferralInfoCard data={data} />
        <DocumentsCard data={data} />

        <NotesCard data={data} />
      </Box>

      <Box sx={{ mt: 2 }}>
        <ReferralsTable referralId={id} />
      </Box>
    </Box>
  );
}
